// One tile on the home page's shelves (Oct 2026): Top picks, Recent tunes,
// Most played, Loved and the More like shelves all draw their tunes with it,
// so Add to playlist and Love sit in the same place on every
// one. The card is a stretched link: the title is the link (or, for a tune
// opened by reference, the button) and its ::after covers the whole card, so
// the card opens the tune while the small buttons in its corner stay buttons
// of their own — a button cannot sit inside a link.

import { Link } from 'react-router-dom';
import type { TasteTune } from '@/state/tuneTaste';
import { TasteButtons, type TasteHides } from './TasteButtons';
import { TileAddToPlaylist } from './TileAddToPlaylist';

type TileKind = 'pick' | 'shelf' | 'recent';

/** Each kind keeps its own classes, and the sizes they set. */
const CARD: Record<TileKind, string> = {
  pick: 'home-pick',
  shelf: 'home-shelf-tile',
  recent: 'home-recent-card',
};
const PART: Record<TileKind, string> = {
  pick: 'home-pick',
  shelf: 'home-shelf',
  recent: 'home-recent',
};

export function HomeTile({
  kind,
  testId,
  reason,
  title,
  melody,
  subtitle,
  foot,
  to,
  onOpen,
  taste,
  hides,
}: {
  kind: TileKind;
  testId?: string;
  /** Why it is here, above the title. */
  reason?: string;
  title: string;
  melody?: boolean;
  /** Under the title (a recent card's composer). */
  subtitle?: string;
  /** Beside the buttons at the foot: the composer, or a recent card's meta. */
  foot?: string;
  /** A link to the tune, or a handler that opens it. */
  to?: string;
  onOpen?: () => void;
  /** The tune the buttons act on: the version it opens, when the card knows. */
  taste: TasteTune;
  hides?: TasteHides;
}) {
  const part = PART[kind];
  const name = (
    <>
      {title}
      {melody && (
        <span className="tune-melody" title="Has a written melody" aria-label="has melody">
          ♪
        </span>
      )}
    </>
  );
  return (
    <div className={`${CARD[kind]} home-tile`} data-testid={testId}>
      {reason && <span className={`${part}-reason`}>{reason}</span>}
      {to !== undefined ? (
        <Link className={`${part}-title home-tile-link`} to={to}>
          {name}
        </Link>
      ) : (
        <button
          type="button"
          className={`${part}-title home-tile-link`}
          onClick={onOpen}
          title={`Open ${title} in the Workbench`}
        >
          {name}
        </button>
      )}
      {subtitle && <span className={`${part}-subtitle muted`}>{subtitle}</span>}
      <div className="home-tile-foot">
        {foot && (
          <span className={kind === 'recent' ? `${part}-meta muted` : `${part}-composer`}>
            {foot}
          </span>
        )}
        <span className="tile-actions">
          <TileAddToPlaylist tune={taste} />
          <TasteButtons tune={taste} hides={hides} />
        </span>
      </div>
    </div>
  );
}
